export enum UserRole {
  Admin = "ADMIN",
  Manager = "MANAGER",
  /** @deprecated */
  Owner = "OWNER",
  Technician = "TECHNICIAN",
  /** Field staff with exactly the permissions of a Technician. */
  Evaluator = "EVALUATOR",
  /** Office staff with exactly the permissions of a Manager. */
  Coordinator = "COORDINATOR",
  Member = "MEMBER",
}

export enum OrderStatus {
  New = "new",
  Proposed = "proposed",
  Upcoming = "upcoming",
  Reviewing = "reviewing",
  Quoting = "quoting",
  Waiting = "waiting",
  Scheduling = "scheduling",
  Scheduled = "scheduled",
  InProgress = "in_progress",
  Billing = "billing",
  Done = "done",
  Deferred = "deferred",
  Cancelled = "cancelled",
  Draft = "draft",
}

export enum OrderPriority {
  Normal = "NORMAL",
  High = "HIGH",
}

export enum OrderType {
  Service = "SERVICE",
  Project = "PROJECT",
  Warranty = "WARRANTY",
}

export enum PlaceType {
  Building = "BUILDING",
  Condo = "CONDO",
  House = "HOUSE",
  Office = "OFFICE",
}

export enum VisitStatus {
  Draft = "DRAFT",
  Pending = "PENDING",
  Declined = "DECLINED",
  Booked = "BOOKED",
  Cancelled = "CANCELLED",
  Expired = "EXPIRED",
  Completed = "COMPLETED",
  Running = "RUNNING",
  Hold = "HOLD",
  Requested = "REQUESTED",
}

export enum TypeOfVisit {
  Onsite = "ONSITE",
  Virtual = "VIRTUAL",
}

export enum PlaceStatus {
  Active = "active",
  Inactive = "inactive",
}

export enum MembershipPlanKey {
  Pro = "pro-membership",
  Home = "home-membership",
  Premier = "premier-membership",
  Lite = "lite-membership",
  Core = "core-membership",
}

export enum MembershipServiceKey {
  MembershipOnboarding = "membership-onboarding",
}

export enum VendorStatus {
  Active = "ACTIVE",
  PendingApproval = "PENDING_APPROVAL",
  DeActivated = "DE_ACTIVATED",
}

export enum AccountType {
  Individual = "INDIVIDUAL",
  Organization = "ORGANIZATION",
  Test = "TEST",
}

/** Account relationship (e.g. for list filters). Matches backend RELATIONSHIP_TYPE_VALUES. */
export enum RelationshipType {
  Customer = "CUSTOMER",
  Vendor = "VENDOR",
}

export enum VendorType {
  ServiceProvider = "SERVICE_PROVIDER",
  IndependentTechnician = "INDEPENDENT_TECHNICIAN",
  Supplier = "SUPPLIER",
}

export enum PlaceRole {
  Owner = "OWNER",
  Assistant = "ASSISTANT",
  Guest = "GUEST",
  Tenant = "TENANT",
  ServiceCoordinator = "SERVICE_COORDINATOR",
  Sponsor = "SPONSOR",
  /** @deprecated Legacy place role; use {@link PlaceRole.ServiceCoordinator}. */
  Manager = "MANAGER",
  /** @deprecated Legacy place role; use {@link PlaceRole.Assistant}. */
  PropertyManager = "PROPERTY_MANAGER",
}

// deprecated
export enum AgentMessageType {
  TOOL = "tool",
  AI = "ai",
  HUMAN = "human",
  SEARCH_STEP = "search_step",
  CUSTOM = "custom",
}

export enum AgentMessageRole {
  User = "user",
  Assistant = "assistant",
}

export enum SubscriptionStatusKey {
  Trial = "trial",
  Active = "active",
  Pending = "pending",
  PastDue = "past-due",
  Paused = "paused",
  Cancelled = "cancelled",
  Finished = "finished",
}

/** Cancel subscription timing: immediate, end of period, or custom date. */
export enum SubscriptionCancelMode {
  Immediate = "immediate",
  EndOfPeriod = "end_of_period",
  CustomDate = "custom_date",
}

/**
 * Which items a delayed charge applies to on `POST /subscriptions/create`.
 *
 * There is deliberately no `Immediate` member: omitting the request's
 * `delayedCharge` object IS the charge-now mode, so a third value here would be
 * a second way to say the same thing.
 */
export enum SubscriptionDelayedChargeMode {
  AllItems = "ALL_ITEMS",
  RecurringOnly = "RECURRING_ONLY",
}

/** Subscription tracking entry keys (e.g. cancel reasons, cancel note). */
export enum SubscriptionTrackingKey {
  CancelReasons = "CANCEL_REASONS",
  CancelNote = "CANCEL_NOTE",
  CancelRequestDate = "CANCEL_REQUEST_DATE",
  CancellationChannel = "CANCELLATION_CHANNEL",
}

/** How the customer requested cancellation (subscription tracking). */
export enum SubscriptionCancellationChannel {
  InApp = "IN_APP",
  Email = "EMAIL",
  Phone = "PHONE",
  Sms = "SMS",
  Offline = "OFFLINE",
}

export enum LineItemType {
  Product = "PRODUCT",
  Service = "SERVICE",
  Labor = "LABOR",
  Fee = "FEE",
}

export enum ProductType {
  Product = "PRODUCT",
  Service = "SERVICE",
  Plan = "PLAN",
}

/** Billing cycle for products/plans. Matches backend PlanBillingCycle. */
export enum BillingCycle {
  Once = "once",
  Monthly = "monthly",
  Annually = "annually",
}

export enum UnitType {
  // Quantity
  Each = "EACH",
  Piece = "PIECE",
  Set = "SET",
  Sheet = "SHEET",
  Item = "ITEM",

  // Length
  Inch = "INCH",
  Foot = "FOOT",
  LinearFoot = "LINEAR_FOOT",
  Yard = "YARD",
  Mile = "MILE",
  Meter = "METER",

  // Area
  SquareFoot = "SQUARE_FOOT",
  SquareYard = "SQUARE_YARD",
  Square = "SQUARE",
  Acre = "ACRE",

  // Volume
  CubicFoot = "CUBIC_FOOT",
  CubicYard = "CUBIC_YARD",
  Gallon = "GALLON",
  Quart = "QUART",

  // Weight
  Pound = "POUND",
  Ton = "TON",

  // Time
  Hour = "HOUR",
  Day = "DAY",
  Week = "WEEK",
  Month = "MONTH",

  // Packaged
  Visit = "VISIT",
  Job = "JOB",
  Project = "PROJECT",
  Install = "INSTALL",

  // Packaged
  Bag = "BAG",
  Box = "BOX",
  Bundle = "BUNDLE",
  LumpSum = "LUMP_SUM",
  Package = "PACKAGE",
}

export enum ScopeItemType {
  LineItem = "LINE_ITEM",
  ItemGroup = "ITEM_GROUP",
}

export enum SubscriptionItemStatus {
  Active = "active",
  Inactive = "inactive",
}

export enum OnboardingStageKey {
  NotStarted = "NOT_STARTED",
  Setup = "SETUP",
  Welcome = "WELCOME",
  Evaluation = "EVALUATION",
  HomeCare = "HOMECARE",
}

export enum TaskStatus {
  Done = "DONE",
  Todo = "TODO",
}

export enum OnboardingWidgetStatus {
  NotStarted = "NOT_STARTED",
  Started = "STARTED",
  Completed = "COMPLETED",
}

export enum CheckoutStatus {
  Pending = "PENDING",
  Success = "SUCCESS",
  Failed = "FAILED",
}

/** `SubscriptionMrrMovement.movement` — how one ledger row moved MRR. */
export enum MrrMovement {
  New = "NEW",
  Expansion = "EXPANSION",
  Contraction = "CONTRACTION",
  Churn = "CHURN",
  Reactivation = "REACTIVATION",
  Pause = "PAUSE",
  Migration = "MIGRATION",
  None = "NONE",
}

export enum ListItemStatus {
  Archived = "ARCHIVED",
  Done = "DONE",
  InProgress = "IN_PROGRESS",
  Todo = "TODO",
}

export enum ListItemPriority {
  Later = "LATER",
  Next = "NEXT",
  NotSet = "NOT_SET",
  Now = "NOW",
}

export enum ListItemFrequency {
  Annual = "ANNUAL",
  BiAnnual = "BI_ANNUAL",
  BiWeekly = "BI_WEEKLY",
  Monthly = "MONTHLY",
  Once = "ONCE",
  Quarterly = "QUARTERLY",
  Weekly = "WEEKLY",
}

export enum ViewType {
  Calendar = "CALENDAR",
  Place = "PLACE",
  Schedule = "SCHEDULE",
  Service = "SERVICE",
  Task = "TASK",
}

export enum UserProfileKey {
  AssignManagerModal = "ASSIGN_MANAGER_MODAL",
  CalendarEventFilterTechnician = "CALENDAR_EVENT_FILTER_TECHNICIAN",
  DownloadAppModal = "DOWNLOAD_APP_MODAL",
  EarlyAccessTodo = "EARLY_ACCESS_TODO",
  GiveFeedback = "GIVE_FEEDBACK",
  GoogleReviewCount = "GOOGLE_REVIEW_COUNT",
  LastUpdatePassword = "LAST_UPDATE_PASSWORD",
  ReceiveNotification = "RECEIVE_NOTIFICATION",
  TimeZone = "TIME_ZONE",
  WelcomeMobile = "WELCOME_MOBILE",
}

/** DB enum has AUDIO (media-v3 voice messages); the GraphQL enum lacked it. */
export enum AttachmentType {
  Audio = "AUDIO",
  File = "FILE",
  Photo = "PHOTO",
  Video = "VIDEO",
}

export enum FollowAction {
  Done = "DONE",
  RequireFollowUp = "REQUIRE_FOLLOW_UP",
}

export enum AccountTrackingKey {
  AcquisitionChannel = "ACQUISITION_CHANNEL",
  AcquisitionSource = "ACQUISITION_SOURCE",
}

export enum ServiceRelationType {
  Duplicated = "DUPLICATED",
  Parent = "PARENT",
  Related = "RELATED",
}

export enum TaskCategory {
  FollowUp = "FOLLOW_UP",
  Invoice = "INVOICE",
  Materials = "MATERIALS",
  Quote = "QUOTE",
}

export enum FeedbackStatus {
  Completed = "COMPLETED",
  Dismissed = "DISMISSED",
  Expired = "EXPIRED",
  Inactive = "INACTIVE",
  Open = "OPEN",
}

export enum CalendarEventItemType {
  GoogleCalendarEvent = "GOOGLE_CALENDAR_EVENT",
  Ooo = "OOO",
  ScheduleEvent = "SCHEDULE_EVENT",
  Visit = "VISIT",
}

export enum MessageViaType {
  Email = "EMAIL",
  Mobile = "MOBILE",
  Web = "WEB",
}

/**
 * Where a message was written — `Message.viaClient`, the column the API reads.
 *
 * Supersedes {@link MessageViaType} for messages and comments; note `Webapp`
 * spells what that enum called `Web`. {@link MessageViaType} remains correct for
 * `Attachment.viaChannel`, which is a different column and keeps its values.
 */
export enum MessageViaClient {
  Email = "EMAIL",
  Mobile = "MOBILE",
  Webapp = "WEBAPP",
}

/**
 * Legacy lowercase order priority. NOT the stored column type — `Order.priority`
 * is `PriorityTypeNew` (`HIGH`/`NORMAL`, see {@link OrderPriority}). V5 still
 * speaks this spelling on exactly two seams:
 *   - `GET /orders/:id` lower-cases `priority` on the way out
 *     (platform/src/resources/orders/order-detail.config.ts:128).
 *   - `POST /orders` accepts `priority: "normal" | "high"`
 *     (platform/src/resources/orders/create-order.dto.ts:38); the same request
 *     also accepts the canonical `servicePriority: "NORMAL" | "HIGH"`.
 * Both directions of the mapping live in platform/src/lib/orders/order-priority.ts.
 */
export enum LegacyOrderPriority {
  High = "high",
  Normal = "normal",
}

/** 89 members, 1:1 with ACTIVITY_TYPE_VALUES. Copy the block from graphql.ts verbatim. */
export enum ActivityType {
  AcceptVisit = "ACCEPT_VISIT",
  AddComment = "ADD_COMMENT",
  AddMessage = "ADD_MESSAGE",
  AddNote = "ADD_NOTE",
  AddReaction = "ADD_REACTION",
  AddServiceNote = "ADD_SERVICE_NOTE",
  AddTask = "ADD_TASK",
  AddVendorToService = "ADD_VENDOR_TO_SERVICE",
  AttachAttachmentToService = "ATTACH_ATTACHMENT_TO_SERVICE",
  BookVisit = "BOOK_VISIT",
  CancelVisit = "CANCEL_VISIT",
  ChangeVisitStatusFromBookedToCompleted = "CHANGE_VISIT_STATUS_FROM_BOOKED_TO_COMPLETED",
  ChangeVisitStatusFromBookedToRunning = "CHANGE_VISIT_STATUS_FROM_BOOKED_TO_RUNNING",
  ChangeVisitStatusFromDraftToBooked = "CHANGE_VISIT_STATUS_FROM_DRAFT_TO_BOOKED",
  ChangeVisitStatusFromDraftToHold = "CHANGE_VISIT_STATUS_FROM_DRAFT_TO_HOLD",
  ChangeVisitStatusFromDraftToPending = "CHANGE_VISIT_STATUS_FROM_DRAFT_TO_PENDING",
  ChangeVisitStatusFromDraftToRequested = "CHANGE_VISIT_STATUS_FROM_DRAFT_TO_REQUESTED",
  ChangeVisitStatusFromHoldToBooked = "CHANGE_VISIT_STATUS_FROM_HOLD_TO_BOOKED",
  ChangeVisitStatusFromHoldToPending = "CHANGE_VISIT_STATUS_FROM_HOLD_TO_PENDING",
  ChangeVisitStatusFromPendingToExpired = "CHANGE_VISIT_STATUS_FROM_PENDING_TO_EXPIRED",
  ChangeVisitStatusFromRunningToCompleted = "CHANGE_VISIT_STATUS_FROM_RUNNING_TO_COMPLETED",
  CompleteTask = "COMPLETE_TASK",
  CompleteVisit = "COMPLETE_VISIT",
  Create = "CREATE",
  CreateADraftVisit = "CREATE_A_DRAFT_VISIT",
  CreateAHoldVisit = "CREATE_A_HOLD_VISIT",
  DeclineVisit = "DECLINE_VISIT",
  Delete = "DELETE",
  DetachAttachmentFromService = "DETACH_ATTACHMENT_FROM_SERVICE",
  FlagService = "FLAG_SERVICE",
  IsOffline = "IS_OFFLINE",
  IsOnline = "IS_ONLINE",
  JoinVisit = "JOIN_VISIT",
  LeaveVisit = "LEAVE_VISIT",
  LinkServiceDuplicated = "LINK_SERVICE_DUPLICATED",
  LinkServiceParent = "LINK_SERVICE_PARENT",
  LinkServiceRelated = "LINK_SERVICE_RELATED",
  MarkAsRead = "MARK_AS_READ",
  RemoveComment = "REMOVE_COMMENT",
  RemoveFeedback = "REMOVE_FEEDBACK",
  RemoveMessage = "REMOVE_MESSAGE",
  RemoveNote = "REMOVE_NOTE",
  RemoveReaction = "REMOVE_REACTION",
  RemoveServiceNote = "REMOVE_SERVICE_NOTE",
  RemoveTask = "REMOVE_TASK",
  RemoveTimePreference = "REMOVE_TIME_PREFERENCE",
  RemoveTimeProposal = "REMOVE_TIME_PROPOSAL",
  RemoveVendorFromService = "REMOVE_VENDOR_FROM_SERVICE",
  RemoveVisit = "REMOVE_VISIT",
  ReAssignVisit = "RE_ASSIGN_VISIT",
  ReBookVisit = "RE_BOOK_VISIT",
  ReTimeProposalRequest = "RE_TIME_PROPOSAL_REQUEST",
  SetPrimaryVendorFromOrder = "SET_PRIMARY_VENDOR_FROM_ORDER",
  ShareTimePreference = "SHARE_TIME_PREFERENCE",
  StartTypingMessage = "START_TYPING_MESSAGE",
  Subscribe = "SUBSCRIBE",
  StartVisit = "START_VISIT",
  StopTypingMessage = "STOP_TYPING_MESSAGE",
  TimePreferenceRequest = "TIME_PREFERENCE_REQUEST",
  TimeProposalRequest = "TIME_PROPOSAL_REQUEST",
  UnflagService = "UNFLAG_SERVICE",
  UnlinkServiceDuplicated = "UNLINK_SERVICE_DUPLICATED",
  UnlinkServiceParent = "UNLINK_SERVICE_PARENT",
  UnlinkServiceRelated = "UNLINK_SERVICE_RELATED",
  Unsubscribe = "UNSUBSCRIBE",
  UpdateArrivalWindow = "UPDATE_ARRIVAL_WINDOW",
  UpdateAssignee = "UPDATE_ASSIGNEE",
  UpdateCategory = "UPDATE_CATEGORY",
  UpdateComment = "UPDATE_COMMENT",
  UpdateDate = "UPDATE_DATE",
  UpdateDescription = "UPDATE_DESCRIPTION",
  UpdateDueDate = "UPDATE_DUE_DATE",
  UpdateInstruction = "UPDATE_INSTRUCTION",
  UpdateManager = "UPDATE_MANAGER",
  UpdateMessage = "UPDATE_MESSAGE",
  UpdateNote = "UPDATE_NOTE",
  UpdatePriority = "UPDATE_PRIORITY",
  UpdateReasonCode = "UPDATE_REASON_CODE",
  UpdateRecipients = "UPDATE_RECIPIENTS",
  UpdateServiceCategories = "UPDATE_SERVICE_CATEGORIES",
  UpdateServiceNote = "UPDATE_SERVICE_NOTE",
  UpdateServicePurpose = "UPDATE_SERVICE_PURPOSE",
  UpdateServiceTrade = "UPDATE_SERVICE_TRADE",
  UpdateServiceType = "UPDATE_SERVICE_TYPE",
  UpdateServiceValue = "UPDATE_SERVICE_VALUE",
  UpdateStatus = "UPDATE_STATUS",
  UpdateSubject = "UPDATE_SUBJECT",
  UpdateTask = "UPDATE_TASK",
  UpdateType = "UPDATE_TYPE",
  UpdateVendors = "UPDATE_VENDORS",
  UpdateVendorFromService = "UPDATE_VENDOR_FROM_SERVICE",
  UpdateVisit = "UPDATE_VISIT",
  UpdateVisitTechnician = "UPDATE_VISIT_TECHNICIAN",
}
